// frontend/src/utils/errorReporter.js
// ─────────────────────────────────────────────────────────────────────────────
// Remontée centralisée des erreurs FRONTEND vers le backend (table incidents).
// Alimente 3 sources : ErrorBoundary (crash React), window.onerror/unhandledrejection
// (erreurs JS non gérées), interceptor axios (erreurs API 5xx / réseau).
//
// Robuste et discret : n'échoue jamais, ne bloque rien, throttle anti-boucle, sendBeacon
// si disponible (survit à un unload). Aucune donnée sensible (le backend re-scrub aussi).

const isDev = process.env.NODE_ENV === 'development';
const API_BASE = isDev
  ? 'http://localhost:5000/api'
  : (process.env.REACT_APP_API_URL || 'https://api.gowele.com/api');
const ENDPOINT = `${API_BASE}/logs/frontend-error`;

// Anti-boucle : même signature (type+message+page) au plus une fois / 30 s.
const THROTTLE_MS = 30 * 1000;
const _lastSent = new Map();
const MAX_KEYS = 200;

function throttled(sig) {
  const now = Date.now();
  const last = _lastSent.get(sig);
  if (last && now - last < THROTTLE_MS) return true;
  _lastSent.set(sig, now);
  if (_lastSent.size > MAX_KEYS) _lastSent.clear();
  return false;
}

/**
 * Envoie un incident frontend. `payload` : { type, message, stack, componentStack, page,
 * http_status, code, reservation_ref }.
 */
export function reportFrontendError(payload = {}) {
  try {
    const body = {
      type: payload.type || 'frontend_unhandled',
      message: String(payload.message || 'Erreur frontend').slice(0, 1000),
      stack: payload.stack ? String(payload.stack).slice(0, 8000) : undefined,
      componentStack: payload.componentStack ? String(payload.componentStack).slice(0, 8000) : undefined,
      page: payload.page || (typeof window !== 'undefined' ? window.location.pathname : ''),
      http_status: Number.isFinite(payload.http_status) ? payload.http_status : undefined,
      code: payload.code || undefined,
      reservation_ref: payload.reservation_ref || undefined,
    };
    const sig = `${body.type}|${body.message}|${body.page}`;
    if (throttled(sig)) return;

    const json = JSON.stringify(body);
    // sendBeacon : fiable même pendant un unload ; fallback fetch keepalive.
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([json], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
    } else if (typeof fetch === 'function') {
      fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: json, keepalive: true }).catch(() => {});
    }
  } catch (_) { /* la télémétrie d'erreur ne doit jamais causer d'erreur */ }
}

// Installe les handlers globaux d'erreurs JS non gérées (une seule fois).
let _installed = false;
export function installGlobalErrorHandlers() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  window.addEventListener('error', (event) => {
    // event.error peut être null (ex. erreurs cross-origin) → on remonte au moins le message.
    reportFrontendError({
      type: 'frontend_unhandled',
      message: event?.message || (event?.error && event.error.message) || 'window.onerror',
      stack: event?.error && event.error.stack,
      page: window.location.pathname,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const r = event?.reason;
    reportFrontendError({
      type: 'frontend_unhandled',
      message: (r && (r.message || String(r))) || 'unhandledrejection',
      stack: r && r.stack,
      page: window.location.pathname,
    });
  });
}
